import React, { useState } from "react";
import CustomButton from "../CustomButton";
import Checkbox from "../Checkbox";
import Api from "../../services/Api";
import { Modal, ModalHeader, ModalBody } from "../Modal";

interface InstructionInterface {
  type: string;
  text: string;
}

interface ClientAgreementInterface {
  type: string;
  clientVersion: number;
  version: number;
  zebrainIsController?: boolean;
  instructions?: Array<InstructionInterface>;
}

interface Props {
  setAgreements: Function;
  clientAgreements: Array<ClientAgreementInterface>;
  lang: string;
}

const ClientAgreements: React.FC<Props> = (props: Props) => {
  const api = Api.getInstance();
  const [allChecked, setAllChecked] = useState<boolean>(false);
  const [checked, setChecked] = useState<Array<boolean>>([false, false, false]);
  const [instructionsModalOpen, setInstructionsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<string>("");

  const [orgInstructionsModalOpen, setOrgInstructionsModalOpen] =
    useState(false);
  const [orgModalInstructions, setOrgModalInstructions] = useState<
    InstructionInterface[] | null
  >(null);

  const handleShowInstructions = (content: string) => {
    setModalContent(content);
    setInstructionsModalOpen(true);
  };

  const handleShowOrgInstructions = (
    instructions: InstructionInterface[] | undefined
  ) => {
    setOrgModalInstructions(instructions ?? null);
    setOrgInstructionsModalOpen(true);
  };

  const renderJsonInstructions = (
    instructions: Array<InstructionInterface>
  ) => {
    return (
      <>
        {instructions.map((instruction, i) => {
          let cls = instruction.type === "header" ? "text-lg" : "";
          if (i > 0) {
            if (instruction.type === "header") {
              cls += " mt-4";
            } else {
              cls += " mt-2";
            }
          }
          return (
            <div className={cls} key={"instruction_" + i}>
              {instruction.text}
            </div>
          );
        })}
      </>
    );
  };

  const renderInstructions = (ca: ClientAgreementInterface, i: number) => {
    if (ca.type === "privacy") {
      return (
        <>
          <div className="flex flex-col text-gray-200 z-title2">
            <label className="flex items-center gap-2 rounded-md cursor-pointer">
              <Checkbox
                id="privacy"
                checked={checked[i]}
                label=""
                onChange={() => toggleCheck(i)}
              />

              <span>
                {props.lang === "sv"
                  ? "Personuppgiftsinformation"
                  : "Personal Data Information"}
              </span>
            </label>
          </div>
          <div className="mt-2 text-gray-200 z-body1 pl-10">
            <div>
              {props.lang === "sv"
                ? "Vi värnar om din integritet. Läs hur vi hanterar och skyddar dina personuppgifter."
                : "We care about your privacy. Learn how we handle and protect your personal data."}
            </div>
            <a
              target="_blank"
              className="cursor-pointer underline font-medium"
              href={
                ca.zebrainIsController
                  ? "https://www.zebrain.se/app/uploads/2025/04/Zebrain_Privacy-Notice_20250107.pdf"
                  : "https://www.zebrain.se/app/uploads/2026/03/Privacy-Notice_Processer_Customer_Controllers_Data_Subjects-20260302.pdf"
              }
            >
              {props.lang === "sv"
                ? "Läs fullständig information här"
                : "Read full details here"}
            </a>
          </div>
        </>
      );
    } else if (ca.type === "zebrain_instructions") {
      return (
        <>
          <div className="flex flex-col text-gray-200 z-title2">
            <label className="flex items-center gap-2 rounded-md cursor-pointer">
              <Checkbox
                id="zebrain_id"
                checked={checked[i]}
                label=""
                onChange={() => toggleCheck(i)}
              />

              <span>
                {props.lang === "sv"
                  ? "Zebrain och AI"
                  : "Zebrain and AI"}
              </span>
            </label>
          </div>
          <div className="mt-2 text-gray-200 z-body1 pl-10">
            <div>
              {props.lang === "sv"
                ? "Zebrain använder AI som en del av plattformen. AI-genererat innehåll kan innehålla fel eller missa viktig kontext. Använd alltid ditt eget omdöme och dubbelkolla viktig information."
                : "Zebrain uses AI as part of the platform. AI-generated content may contain errors or miss important context – always use your own judgment and double-check important information."}
            </div>
            {/*<div>*/}
            {/*  <span*/}
            {/*    className="cursor-pointer underline font-medium"*/}
            {/*    onClick={() =>*/}
            {/*      handleShowInstructions(*/}
            {/*        props.lang === "sv"*/}
            {/*          ? "1. instruktioner från Zebrain\n2. instruktioner från Zebrain\n3. instruktioner från Zebrain"*/}
            {/*          : "1. instructions from Zebrain\n2. instructions from Zebrain\n3. instructions from Zebrain"*/}
            {/*      )*/}
            {/*    }*/}
            {/*  >*/}
            {/*    {props.lang === "sv" ? "Läs policyn" : "Read the policy"}*/}
            {/*  </span>*/}
            {/*</div>*/}
          </div>
        </>
      );
    } else if (ca.instructions) {
      return (
        <>
          <div className="flex flex-col text-gray-200 z-title2">
            <label className="flex items-center gap-2 rounded-md cursor-pointer">
              <Checkbox
                id="instruction_id"
                checked={checked[i]}
                label=""
                onChange={() => toggleCheck(i)}
              />

              <span>
                {props.lang === "sv"
                  ? "Riktlinjer från din organisation"
                  : "Guidelines from your organization"}
              </span>
            </label>
          </div>

          <div className="mt-2 text-gray-200 z-body1 pl-10">
            <div>
              {props.lang === "sv"
                ? "Här hittar du ytterligare riktlinjer från din organisation."
                : "Here you’ll find additional guidelines provided by your organization."}
            </div>
            <div>
              <span
                className="cursor-pointer underline font-medium"
                onClick={() => handleShowOrgInstructions(ca.instructions)}
              >
                {props.lang === "sv"
                  ? "Se fullständiga instruktioner"
                  : "See full instructions"}
              </span>
            </div>
          </div>
        </>
      );
    }
  };

  const acceptAgreements = () => {
    api.get("client/accept_agreements").then((result) => {
      props.setAgreements([]);
    });
  };

  const toggleCheck = (i: number) => {
    const _checked = [...checked];
    let approvedAgreements = 0;
    _checked[i] = !checked[i];
    for (let c of _checked) {
      if (c) {
        approvedAgreements++;
      }
    }
    setChecked(_checked);
    if (approvedAgreements === props.clientAgreements.length) {
      setAllChecked(true);
    } else {
      setAllChecked(false);
    }
  };

  return (
    <div className="z-all-window">
      <div className="z-all-middle-col p-4 desktop:mt-[146px] mt-8">
        <div className="text-gray-200 z-title1">
          {props.lang === "sv"
            ? "Välkommen till Zebrain! Innan du börjar, vänligen ta en stund för att läsa igenom och godkänna villkoren nedan."
            : "Welcome to Zebrain! Before you get started, please take a moment to review and agree to the terms below."}
        </div>
        {props.clientAgreements.map((clientAgreement, i) => {
          return (
            <div className={i === 0 ? "mt-12" : "mt-6"} key={"agreement_" + i}>
              {renderInstructions(clientAgreement, i)}
            </div>
          );
        })}

        <CustomButton
          className="mt-8"
          disabled={!allChecked}
          onClick={() => acceptAgreements()}
        >
          {allChecked
            ? props.lang === "sv"
              ? "Fortsätt"
              : "Continue"
            : props.lang === "sv"
            ? "Klicka i att du accepterar ovanstående"
            : "Check the boxes above to preceed"}
        </CustomButton>
      </div>
      <Modal
        isOpen={instructionsModalOpen}
        onClose={() => setInstructionsModalOpen(false)}
      >
        <ModalHeader onClose={() => setInstructionsModalOpen(false)}>
          <div className="z-title1">
            {props.lang === "sv"
              ? "Fullständiga instruktioner"
              : "Full instructions"}
          </div>
        </ModalHeader>
        <ModalBody>
          {modalContent.split("\n").map((line, idx) => (
            <div className="text-gray-200" key={idx}>
              {line}
            </div>
          ))}
        </ModalBody>
      </Modal>
      <Modal
        isOpen={orgInstructionsModalOpen}
        onClose={() => setOrgInstructionsModalOpen(false)}
      >
        <ModalHeader onClose={() => setOrgInstructionsModalOpen(false)}>
          <div className="z-title1">
            {props.lang === "sv"
              ? "Instruktioner från din organisation"
              : "Instructions from your organization"}
          </div>
        </ModalHeader>
        <ModalBody>
          {orgModalInstructions && renderJsonInstructions(orgModalInstructions)}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ClientAgreements;
